/**
 * Per-user selection of which fields the conversation advanced-filter panel
 * renders. Mirrors the inbox column preference: a localStorage seed for an
 * instant first paint, overridden by the server copy
 * (`users.conversation_filter_fields`) once it arrives, and written to both on
 * save so the choice follows the agent across devices.
 */
import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'
import {
  DEFAULT_FILTER_FIELDS,
  sanitizeFilterFields,
  type FilterFieldKey,
} from '../components/conversations/filterFields.js'

const STORAGE_KEY = 'kc3_conversation_filter_fields'

function readSeed(): FilterFieldKey[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? sanitizeFilterFields(JSON.parse(raw)) : DEFAULT_FILTER_FIELDS
  } catch {
    return DEFAULT_FILTER_FIELDS
  }
}

function writeSeed(fields: FilterFieldKey[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(fields))
  } catch {
    /* preference only — a full/blocked localStorage must not break the panel */
  }
}

export interface UseConversationFilterFieldsResult {
  fields: FilterFieldKey[]
  /** Persist a new selection (optimistic locally, fire-and-forget to the API). */
  save: (next: FilterFieldKey[]) => void
  /** Restore the app defaults. */
  reset: () => void
}

export function useConversationFilterFields(): UseConversationFilterFieldsResult {
  const [fields, setFields] = useState<FilterFieldKey[]>(readSeed)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const res = await api.get<{ fields: string[] }>('/agent/me/filter-fields')
        // An empty array is "never customised" — keep the defaults rather than
        // overriding a local seed with nothing.
        if (cancelled || !Array.isArray(res.fields) || res.fields.length === 0) return
        const next = sanitizeFilterFields(res.fields)
        setFields(next)
        writeSeed(next)
      } catch {
        /* fall back to the localStorage seed / defaults */
      }
    })()
    return () => { cancelled = true }
  }, [])

  const persist = useCallback((next: FilterFieldKey[]): void => {
    setFields(next)
    writeSeed(next)
    void api.put('/agent/me/filter-fields', { fields: next }).catch(() => { /* best-effort */ })
  }, [])

  const save = useCallback((next: FilterFieldKey[]): void => {
    persist(sanitizeFilterFields(next))
  }, [persist])

  const reset = useCallback((): void => {
    persist(DEFAULT_FILTER_FIELDS)
  }, [persist])

  return { fields, save, reset }
}
